import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Trash2, Users, UserPlus, UserX, Shield, Building2, Mail, Send, MoreHorizontal, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar } from "@/components/shared";

export const TeamView = ({ teams, onCreateTeam, onUpdateTeam, onDeleteTeam, onSelectTeam, selectedTeam, teamDetails, onInviteMember, onRemoveMember, onUpdateRole, onCancelInvite, onTransferOwnership }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [editTeam, setEditTeam] = useState({ name: '', description: '' });
  const [transferTo, setTransferTo] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const ROLE_COLORS = { owner: 'bg-amber-500/10 text-amber-600', admin: 'bg-purple-500/10 text-purple-600', member: 'bg-blue-500/10 text-blue-600', viewer: 'bg-slate-500/10 text-slate-600' };

  const handleCreate = async () => {
    if (!newTeam.name.trim()) { toast.error('Name required'); return; }
    await onCreateTeam(newTeam);
    setNewTeam({ name: '', description: '' });
    setShowCreateModal(false);
  };

  const handleOpenEdit = () => {
    setEditTeam({ name: teamDetails.name || '', description: teamDetails.description || '' });
    setTransferTo('');
    setShowEditModal(true);
  };

  const handleTransfer = async () => {
    if (!transferTo) return;
    const target = teamDetails.members?.find(m => m.user_id === transferTo);
    const targetName = target?.user?.name || target?.user?.email || 'this member';
    if (!window.confirm(`Transfer ownership to ${targetName}? You will become an admin and lose owner privileges.`)) return;
    await onTransferOwnership(selectedTeam, transferTo);
    setTransferTo('');
    setShowEditModal(false);
  };

  const handleSaveEdit = async () => {
    if (!editTeam.name.trim()) { toast.error('Name required'); return; }
    await onUpdateTeam(selectedTeam, editTeam);
    setShowEditModal(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Email required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) { toast.error('Invalid email'); return; }
    await onInviteMember(selectedTeam, inviteEmail, inviteRole);
    setInviteEmail('');
    setInviteRole('member');
    setShowInviteModal(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" />Teams</h2>
        <Button onClick={() => setShowCreateModal(true)}><Plus className="h-4 w-4 mr-2" />New Team</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          {teams.length === 0 ? (
            <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No teams yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Create a team to collaborate</p>
              <Button variant="outline" onClick={() => setShowCreateModal(true)}><Plus className="h-4 w-4 mr-2" />Create Team</Button>
            </div>
          ) : (
            teams.map(team => (
              <button key={team.id} onClick={() => onSelectTeam(team.id)} className={`w-full text-left p-4 bg-card rounded-xl border transition-all ${selectedTeam === team.id ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`}>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg"><Building2 className="h-5 w-5 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{team.name}</h4>
                    <p className="text-xs text-muted-foreground">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</p>
                  </div>
                  <Badge variant="secondary" className={ROLE_COLORS[team.my_role]}>{team.my_role}</Badge>
                </div>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {selectedTeam && teamDetails ? (
            <div className="bg-card rounded-xl border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold">{teamDetails.name}</h3>
                  {teamDetails.description && <p className="text-sm text-muted-foreground">{teamDetails.description}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowInviteModal(true)} data-testid="team-invite-btn"><UserPlus className="h-4 w-4 mr-2" />Invite</Button>
                  {(teamDetails.my_role === 'owner' || teamDetails.my_role === 'admin') && (<Button variant="outline" size="icon" onClick={handleOpenEdit} data-testid="team-edit-btn" title="Edit team"><Pencil className="h-4 w-4" /></Button>)}
                  {teamDetails.my_role === 'owner' && (<Button variant="destructive" size="icon" onClick={() => onDeleteTeam(selectedTeam)} data-testid="team-delete-btn" title="Delete team"><Trash2 className="h-4 w-4" /></Button>)}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Users className="h-4 w-4" />Members ({teamDetails.members?.length || 0})</h4>
                <div className="space-y-2">
                  {teamDetails.members?.map(member => (
                    <div key={member.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg group">
                      {member.user?.picture ? (<img src={member.user.picture} alt="" className="w-10 h-10 rounded-full" />) : (<Avatar name={member.user?.name || member.user?.email || 'User'} size="md" />)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{member.user?.name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground truncate">{member.user?.email}</p>
                      </div>
                      <Select value={member.role} onValueChange={(v) => onUpdateRole(selectedTeam, member.user_id, v)} disabled={member.role === 'owner'}>
                        <SelectTrigger className={`w-28 h-8 ${ROLE_COLORS[member.role]}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                      {member.role !== 'owner' && (<Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 text-destructive" onClick={() => onRemoveMember(selectedTeam, member.user_id)}><UserX className="h-4 w-4" /></Button>)}
                    </div>
                  ))}
                </div>
              </div>

              {teamDetails.pending_invites?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><Mail className="h-4 w-4" />Pending Invites</h4>
                  <div className="space-y-2">
                    {teamDetails.pending_invites.map(invite => (
                      <div key={invite.id} className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                        <Mail className="h-5 w-5 text-amber-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{invite.email}</p>
                          <p className="text-xs text-muted-foreground">Invited as {invite.role}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => onCancelInvite(selectedTeam, invite.id)}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card rounded-xl border p-6 flex items-center justify-center h-64 text-muted-foreground">Select a team to view details</div>
          )}
        </div>
      </div>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" />Create Team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1.5 block">Team Name</label><Input placeholder="e.g., Product Team" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Description</label><Textarea placeholder="What does this team work on?" value={newTeam.description} onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button><Button onClick={handleCreate}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" />Invite Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1.5 block">Email Address</label><Input type="email" placeholder="colleague@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="h-4 w-4" />Admin - Full access</div></SelectItem>
                  <SelectItem value="member"><div className="flex items-center gap-2"><Users className="h-4 w-4" />Member - Can edit</div></SelectItem>
                  <SelectItem value="viewer"><div className="flex items-center gap-2"><Users className="h-4 w-4" />Viewer - Read only</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button><Button onClick={handleInvite}><Send className="h-4 w-4 mr-2" />Send Invite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" />Edit Team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1.5 block">Team Name</label><Input placeholder="e.g., Product Team" value={editTeam.name} onChange={(e) => setEditTeam({ ...editTeam, name: e.target.value })} data-testid="team-edit-name-input" /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Description</label><Textarea placeholder="What does this team work on?" value={editTeam.description} onChange={(e) => setEditTeam({ ...editTeam, description: e.target.value })} rows={2} data-testid="team-edit-description-input" /></div>
            {teamDetails?.my_role === 'owner' && teamDetails?.members?.some(m => m.role !== 'owner') && (
              <div className="pt-3 border-t">
                <label className="text-sm font-medium mb-1.5 flex items-center gap-2"><Shield className="h-4 w-4 text-amber-500" />Transfer Ownership</label>
                <div className="flex gap-2">
                  <Select value={transferTo} onValueChange={setTransferTo}>
                    <SelectTrigger className="flex-1" data-testid="transfer-owner-select"><SelectValue placeholder="Select new owner" /></SelectTrigger>
                    <SelectContent>
                      {teamDetails.members.filter(m => m.role !== 'owner').map(m => (
                        <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email || 'User'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" disabled={!transferTo} onClick={handleTransfer} data-testid="transfer-owner-btn">Transfer</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">You will become an admin after transferring ownership.</p>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button><Button onClick={handleSaveEdit} data-testid="team-edit-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
